import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { trackingApi } from '@/services/api'
import type { TrackingCategory } from '@/types'
import { Tag, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'

export default function TrackingCategories() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [categories, setCategories] = useState<TrackingCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [premiumGated, setPremiumGated] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [addingCat, setAddingCat] = useState(false)
  const [newOptionName, setNewOptionName] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!clientId) return
    trackingApi.list(clientId)
      .then((res) => {
        setCategories(res.data.categories)
        const exp: Record<string, boolean> = {}
        for (const c of res.data.categories) exp[c.id] = true
        setExpanded(exp)
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 402) setPremiumGated(true)
        else toast.error('Failed to load tracking categories')
      })
      .finally(() => setLoading(false))
  }, [clientId])

  async function addCategory(e: React.FormEvent) {
    e.preventDefault()
    if (!clientId || !newCatName.trim()) return
    setAddingCat(true)
    try {
      const res = await trackingApi.createCategory({ client_ledger_id: clientId, name: newCatName.trim() })
      setCategories((prev) => [...prev, res.data])
      setExpanded((e) => ({ ...e, [res.data.id]: true }))
      setNewCatName('')
      toast.success('Category created')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create category'
      toast.error(msg)
    } finally {
      setAddingCat(false)
    }
  }

  async function addOption(categoryId: string, e: React.FormEvent) {
    e.preventDefault()
    const name = newOptionName[categoryId]?.trim()
    if (!name) return
    try {
      const res = await trackingApi.createOption(categoryId, { name })
      setCategories((prev) => prev.map((c) => c.id === categoryId ? res.data : c))
      setNewOptionName((prev) => ({ ...prev, [categoryId]: '' }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to add option'
      toast.error(msg)
    }
  }

  async function removeOption(categoryId: string, optionId: string) {
    try {
      await trackingApi.deleteOption(optionId)
      setCategories((prev) => prev.map((c) =>
        c.id === categoryId ? { ...c, options: c.options.filter((o) => o.id !== optionId) } : c
      ))
    } catch {
      toast.error('Failed to remove option')
    }
  }

  async function toggleCategory(cat: TrackingCategory) {
    try {
      const res = await trackingApi.updateCategory(cat.id, { is_active: !cat.is_active })
      setCategories((prev) => prev.map((c) => c.id === cat.id ? res.data : c))
    } catch {
      toast.error('Failed to update category')
    }
  }

  if (premiumGated) {
    return (
      <div className="max-w-sm mx-auto mt-16">
        <div className="card p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Tag className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Tracking categories require a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button variant="default" onClick={() => navigate(`/clients/${clientId}/settings`)}>
            Upgrade to Premium
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Tracking Categories</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Segment transactions by department, project, or any dimension</p>
        </div>
      </div>

      {/* Add new category */}
      <form onSubmit={addCategory} className="card p-4 mb-5 flex gap-3">
        <input
          value={newCatName}
          onChange={(e) => setNewCatName(e.target.value)}
          placeholder="New category name (e.g. Department)"
          className="input flex-1"
          required
        />
        <Button type="submit" variant="create" disabled={addingCat}>
          <Plus className="w-4 h-4" />
          {addingCat ? 'Adding...' : 'Add Category'}
        </Button>
      </form>

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : categories.length === 0 ? (
        <p className="text-center text-[13px] text-[#CCCED2] py-8">No tracking categories yet</p>
      ) : (
        <div className="space-y-3">
          {categories.map((cat) => (
            <div key={cat.id} className="card overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-surface-50"
                onClick={() => setExpanded((e) => ({ ...e, [cat.id]: !e[cat.id] }))}
              >
                <div className="flex items-center gap-2">
                  {expanded[cat.id] ? <ChevronUp className="w-4 h-4 text-[#CCCED2]" /> : <ChevronDown className="w-4 h-4 text-[#CCCED2]" />}
                  <span className="text-[13px] font-medium text-[#000A1E]">{cat.name}</span>
                  <span className="text-[13px] text-[#CCCED2]">({cat.options.length} options)</span>
                  {!cat.is_active && <span className="text-[13px] text-amber-500">inactive</span>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleCategory(cat) }}
                  className="text-[13px] text-[rgba(0,10,30,0.75)] hover:text-[#000A1E] px-2 py-1 rounded hover:bg-surface-100"
                >
                  {cat.is_active ? 'Disable' : 'Enable'}
                </button>
              </div>

              {expanded[cat.id] && (
                <div className="border-t border-surface-100 px-4 py-3 space-y-2">
                  {cat.options.map((opt) => (
                    <div key={opt.id} className="flex items-center justify-between py-1">
                      <span className="text-[13px] text-[#000A1E]">{opt.name}</span>
                      <button
                        onClick={() => removeOption(cat.id, opt.id)}
                        className="btn-ghost p-1 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <form onSubmit={(e) => addOption(cat.id, e)} className="flex gap-2 mt-2">
                    <input
                      value={newOptionName[cat.id] || ''}
                      onChange={(e) => setNewOptionName((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                      placeholder="Add option..."
                      className="input flex-1 text-sm"
                    />
                    <Button type="submit" variant="outline" size="sm">Add</Button>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
